import { Injectable } from '@angular/core';
import {
  AnchorResult,
  BoundingBoxAnchorResult,
  ConsistencyIssuesToUpdateDto,
  CopiedAnchorResult,
  ReferencePhrase,
  UserAddedAnchorResult
} from '@shared/models/common';
import { IndElement, IndPhrase } from '@shared/models/cat.model';
import { RevRule } from '@shared/models/rev.model';
import { EmbeddedObjectAnnotation } from '@shared/models/embedded-object-annotation';

type AnchorEditableElement = IndPhrase | RevRule;

export interface ClaimToolAnchorState {
  references: ReferencePhrase[];
  deletedReferences: ReferencePhrase[];
  consistencyIssuesToUpdate: ConsistencyIssuesToUpdateDto | null;
  anchorsPathsToUpdate: IndPhrase['anchorsPathsToUpdate'];
  boundingBoxAnchorsToAdd: BoundingBoxAnchorResult[];
  copiedAnchors: CopiedAnchorResult[];
}

export interface ClaimToolIssueState {
  baseline: Record<string, unknown>;
  values: Record<string, unknown>;
  associatedImages: Array<{ assocImageId: number; baseline: unknown; value: unknown }>;
}

@Injectable({ providedIn: 'root' })
export class ClaimToolReturnStateService {
  mergeCurrentChanges(local: IndElement | RevRule, fresh: IndElement | RevRule): void {
    const baseline = local.original ? JSON.parse(local.original) as Record<string, unknown> : {};
    const issueState = this.captureIssues(local, baseline);
    const anchorState = local instanceof IndPhrase || local instanceof RevRule
      ? this.captureAnchors(local)
      : null;
    const excludedFields = new Set([
      'original', 'propagateActionFromParent', 'references', 'deletedReferences', 'catMatches', 'ruleCatMatches',
      'consistencyIssuesToUpdate', 'anchorsPathsToUpdate', 'boundingBoxAnchorsToAdd', 'copiedAnchors',
      'children', 'contentChildren', 'imageChildren', 'parentPhraseId', 'isParentActionSelected', 'viewSw', 'checkedSw',
      'consistencyIssues', 'fairBalanceIssues', 'complianceIssues', 'consistencyIssuesDirty',
      'isDirtyConsistencyCheckResults', 'disableConsistencyCheckResultsActions', 'associatedImages'
    ]);
    const localValues = local as unknown as Record<string, unknown>;
    const freshValues = fresh as unknown as Record<string, unknown>;

    Object.keys(localValues).filter(field => !excludedFields.has(field)).forEach(field => {
      if (!this.equal(localValues[field], baseline[field]) && this.equal(freshValues[field], baseline[field])) {
        freshValues[field] = localValues[field];
      }
    });

    this.restoreIssues(fresh, issueState);

    if (anchorState && (fresh instanceof IndPhrase || fresh instanceof RevRule)) {
      this.restoreAnchors(fresh, anchorState);
    }
  }

  captureIssues(element: IndElement | RevRule, baseline?: Record<string, unknown>): ClaimToolIssueState {
    const original = baseline ?? (element.original ? JSON.parse(element.original) as Record<string, unknown> : {});
    const current = element as unknown as Record<string, unknown>;
    const fields = [
      'consistencyIssues', 'fairBalanceIssues', 'complianceIssues', 'consistencyIssuesDirty',
      'isDirtyConsistencyCheckResults', 'disableConsistencyCheckResultsActions'
    ];
    const values: Record<string, unknown> = {};
    fields.forEach(field => {
      values[field] = Array.isArray(current[field]) ? [...current[field] as unknown[]] : current[field];
    });
    const baselineImages = (original.associatedImages ?? []) as Array<Record<string, unknown>>;
    const currentImages = (current.associatedImages ?? []) as Array<Record<string, unknown>>;

    return {
      baseline: original,
      values,
      associatedImages: currentImages.map(image => ({
        assocImageId: image.assocImageId as number,
        baseline: baselineImages.find(originalImage => originalImage.assocImageId === image.assocImageId)?.consistencyIssues,
        value: Array.isArray(image.consistencyIssues) ? [...image.consistencyIssues as unknown[]] : image.consistencyIssues
      }))
    };
  }

  hasIssueChanges(state: ClaimToolIssueState): boolean {
    return Object.keys(state.values).some(field => !this.equal(state.values[field], state.baseline[field])) ||
      state.associatedImages.some(image => !this.equal(image.value, image.baseline));
  }

  restoreIssues(element: IndElement | RevRule, state: ClaimToolIssueState): void {
    const current = element as unknown as Record<string, unknown>;
    const fields = [
      'consistencyIssues', 'fairBalanceIssues', 'complianceIssues', 'consistencyIssuesDirty',
      'isDirtyConsistencyCheckResults', 'disableConsistencyCheckResultsActions'
    ];
    fields.forEach(field => {
      const localValue = state.values[field];
      if (!this.equal(localValue, state.baseline[field]) && this.equal(current[field], state.baseline[field])) {
        current[field] = localValue;
      }
    });

    const freshImages = (current.associatedImages ?? []) as Array<Record<string, unknown>>;
    state.associatedImages.forEach(localImage => {
      const freshImage = freshImages.find(image => image.assocImageId === localImage.assocImageId);
      if (freshImage && !this.equal(localImage.value, localImage.baseline) &&
          this.equal(freshImage.consistencyIssues, localImage.baseline)) {
        freshImage.consistencyIssues = localImage.value;
      }
    });
  }

  captureAnchors(element: AnchorEditableElement): ClaimToolAnchorState {
    return {
      references: (element.references ?? []).map(reference => this.cloneReference(reference)),
      deletedReferences: ((element as IndPhrase).deletedReferences ?? []).map(reference => this.cloneReference(reference)),
      consistencyIssuesToUpdate: this.cloneJson(element.consistencyIssuesToUpdate),
      anchorsPathsToUpdate: this.cloneJson(element.anchorsPathsToUpdate),
      boundingBoxAnchorsToAdd: (element.boundingBoxAnchorsToAdd ?? []).map(anchor => this.cloneAnchor(anchor) as BoundingBoxAnchorResult),
      copiedAnchors: ((element as IndPhrase).copiedAnchors ?? []).map(anchor => this.cloneAnchor(anchor) as CopiedAnchorResult)
    };
  }

  restoreAnchors(element: AnchorEditableElement, state: ClaimToolAnchorState): void {
    const freshReferences = element.references ?? [];
    const missingReferences = state.references.filter(reference =>
      !reference.newSw && reference.refPhraseId != null &&
      !freshReferences.some(fresh => fresh.refPhraseId === reference.refPhraseId)
    );
    const deletedReferences = state.deletedReferences;
    const removedReferenceDocIds = new Set<number>([
      ...missingReferences.map(reference => reference.refDocId),
      ...deletedReferences.map(reference => reference.refDocId)
    ].filter((id): id is number => id != null));

    element.references = freshReferences.filter(reference =>
      !deletedReferences.some(deleted => deleted.refPhraseId === reference.refPhraseId)
    );
    if (element instanceof IndPhrase) {
      element.deletedReferences = deletedReferences.map(reference => this.cloneReference(reference));
    }

    state.references.filter(reference => reference.newSw).forEach(localReference => {
      const exists = element.references.some(reference =>
        reference.refDocId === localReference.refDocId
      );
      if (!exists) {
        element.references.push(this.cloneReference(localReference));
      }
    });

    const retainedRefDocIds = new Set(element.references.map(reference => reference.refDocId).filter((id): id is number => id != null));
    retainedRefDocIds.forEach(id => removedReferenceDocIds.delete(id));

    const updatePayload = this.cloneJson(state.consistencyIssuesToUpdate);
    if (updatePayload) {
      updatePayload.add.phrase_reference_consistency_check_results =
        (updatePayload.add.phrase_reference_consistency_check_results ?? []).filter(item => !removedReferenceDocIds.has(item.ref_doc_id));
      updatePayload.remove.phrase_reference_consistency_check_results =
        (updatePayload.remove.phrase_reference_consistency_check_results ?? []).filter(item => !removedReferenceDocIds.has(item.ref_doc_id));
      updatePayload.convert.anchors =
        (updatePayload.convert.anchors ?? []).filter(item => !removedReferenceDocIds.has(item.ref_doc_id));
      element.consistencyIssuesToUpdate = updatePayload;
    }

    const anchorPathUpdates = this.cloneJson(state.anchorsPathsToUpdate);
    if (anchorPathUpdates) {
      anchorPathUpdates.anchors = anchorPathUpdates.anchors.filter(anchor => !removedReferenceDocIds.has(anchor.ref_doc_id));
      element.anchorsPathsToUpdate = anchorPathUpdates.anchors.length ? anchorPathUpdates : null;
    }
    element.boundingBoxAnchorsToAdd = state.boundingBoxAnchorsToAdd
      .filter(anchor => !removedReferenceDocIds.has(anchor.refDocId))
      .map(anchor => this.cloneAnchor(anchor) as BoundingBoxAnchorResult);
    if (element instanceof IndPhrase) {
      element.copiedAnchors = state.copiedAnchors
        .filter(anchor => !removedReferenceDocIds.has(anchor.refDocId))
        .map(anchor => this.cloneAnchor(anchor) as CopiedAnchorResult);
    }

    element.references.forEach(freshReference => {
      // a locally added reference may already exist in fresh data under the same doc; still apply its local anchor changes
      const localReference = state.references.find(reference =>
        reference.refPhraseId === freshReference.refPhraseId && reference.refDocId === freshReference.refDocId
      ) ?? state.references.find(reference => reference.newSw && reference.refDocId === freshReference.refDocId);
      if (localReference) {
        this.mergeReferenceAnchors(freshReference, localReference, updatePayload, anchorPathUpdates, element.boundingBoxAnchorsToAdd);
      }
    });
  }



  private mergeReferenceAnchors(
    fresh: ReferencePhrase,
    local: ReferencePhrase,
    updatePayload: ConsistencyIssuesToUpdateDto | null,
    pathUpdates: IndPhrase['anchorsPathsToUpdate'],
    boundingBoxAnchors: BoundingBoxAnchorResult[]
  ): void {
    const refDocId = fresh.refDocId ?? fresh.anchors?.[0]?.refDocId;
    if (refDocId == null) return;

    const baseline = local.original ? JSON.parse(local.original) as Record<string, unknown> : {};
    (['refPhraseText', 'refCatMatchTxt', 'pointerWordId'] as const).forEach(field => {
      if (!this.equal(local[field], baseline[field]) && this.equal(fresh[field], baseline[field])) {
        (fresh as unknown as Record<string, unknown>)[field] = local[field];
      }
    });

    const removals = updatePayload?.remove.phrase_reference_consistency_check_results ?? [];
    fresh.anchors = (fresh.anchors ?? []).filter(anchor =>
      !removals.some(removed => removed.ref_doc_id === anchor.refDocId && removed.ref_doc_anchor_id === anchor.refDocAnchorId)
    );

    const additions = updatePayload?.add.phrase_reference_consistency_check_results ?? [];
    (local.anchors ?? []).forEach(localAnchor => {
      const pendingAdd = additions.some(added => added.ref_doc_id === refDocId && (
        added.ref_doc_anchor_id === localAnchor.refDocAnchorId ||
        (added.ref_doc_anchor_id == null && localAnchor.refDocAnchorId == null && added.ref_doc_anchor_path === localAnchor.embeddedObjectAnnotation?.anchorPath)
      )) || boundingBoxAnchors.some(anchor => anchor.refDocId === refDocId && anchor.refDocAnchorId == null &&
        anchor.embeddedObjectAnnotation?.anchorPath === localAnchor.embeddedObjectAnnotation?.anchorPath);
      const present = fresh.anchors.some(anchor => anchor.refDocId === refDocId && anchor.refDocAnchorId === localAnchor.refDocAnchorId &&
        (anchor.refDocAnchorId != null || anchor.embeddedObjectAnnotation?.anchorPath === localAnchor.embeddedObjectAnnotation?.anchorPath));
      if (pendingAdd && !present) {
        fresh.anchors.push(this.cloneAnchor(localAnchor));
      }
    });

    (pathUpdates?.anchors ?? []).filter(update => update.ref_doc_id === refDocId).forEach(update => {
      const anchor = fresh.anchors?.find(item => item.refDocId === refDocId && item.refDocAnchorId === update.ref_doc_anchor_id);
      if (anchor?.embeddedObjectAnnotation) anchor.embeddedObjectAnnotation.anchorPath = update.data.path;
    });
    if (local.isDeletedAnchor && !fresh.anchors?.length) fresh.isDeletedAnchor = true;
  }

  private cloneReference(reference: ReferencePhrase): ReferencePhrase {
    const cloned = new ReferencePhrase();
    Object.assign(cloned, this.cloneJson(reference));
    cloned.anchors = (reference.anchors ?? []).map(anchor => this.cloneAnchor(anchor));
    return cloned;
  }

  private cloneAnchor(anchor: AnchorResult): AnchorResult {
    const annotation = new EmbeddedObjectAnnotation();
    Object.assign(annotation, this.cloneJson(anchor.embeddedObjectAnnotation));
    let cloned: AnchorResult;
    if (anchor instanceof BoundingBoxAnchorResult) {
      cloned = new BoundingBoxAnchorResult(anchor.issueId, anchor.refDocAnchorId, anchor.refDocId, annotation);
    } else if (anchor instanceof CopiedAnchorResult) {
      cloned = new CopiedAnchorResult(anchor.issueId, anchor.refDocAnchorId, anchor.refDocId, annotation);
    } else if (anchor instanceof UserAddedAnchorResult) {
      cloned = new UserAddedAnchorResult(anchor.issueId, anchor.refDocAnchorId, anchor.refDocId, annotation);
    } else {
      cloned = new AnchorResult(anchor.issueId, anchor.refDocAnchorId, anchor.refDocId, annotation);
    }
    Object.assign(cloned, this.cloneJson(anchor));
    cloned.embeddedObjectAnnotation = annotation;
    return cloned;
  }

  private cloneJson<T>(value: T): T {
    return value == null ? value : JSON.parse(JSON.stringify(value));
  }

  private equal(first: unknown, second: unknown): boolean {
    return JSON.stringify(first) === JSON.stringify(second);
  }
}